import { useEffect, useState } from 'react'
import LayoutManager from './layout/LayoutManager'
import { ToastProvider } from './layout/ToastContext'
import { AuthProvider, useAuth } from './layout/AuthContext'
import Login from './pages/Login'
import { getHealth, getMailMode, Health } from './api'
import { BUILD_INFO_POLL_MS } from '../shared/constants'
import { fill, CLIENT_APP_TEXTS as T } from '../shared/texts'

// Auth gate: resolve the session, then show either the login screen or the app.
function Gate() {
    const { user, loading } = useAuth()
    if (loading) {
        return (
            <div className="login-page">
                <img src="/logo-deja.png" alt={T.logoAlt} className="login-logo" />
            </div>
        )
    }
    if (!user) return <Login />
    return (
        <ToastProvider>
            <LayoutManager />
        </ToastProvider>
    )
}

// The band's text. Two shapes, and the DATA decides which — never a hostname (see below):
//
//   • build stamp present → "Entwicklungsumgebung · Nightly build (12.09.2026, 09:24) · cf811f8"
//   • no stamp            → "lokale Entwicklungsumgebung"
//
// ⚠️ „lokale" is the operator's own wording (12.09.2026), chosen over the neutral
// „Entwicklungsumgebung — nicht das Live-Backoffice" that stood here first. Know what it
// assumes: a missing build stamp does NOT prove „local". It would also be missing if the
// Dockerfile ARG or the daily workflow's build-args were ever dropped — and then
// dev.backoffice.deja.ltd would quietly call itself somebody's laptop. The case is
// hypothetical while the nightly build bakes BUILD_DATE in (then dev shows the stamp and
// never this text), and the decision was made with that trade-off on the table. If you
// are reading this because a server is claiming to be local: the stamp is missing, look
// at the workflow's build-args and the Dockerfile, not at this string.
const LOKAL = T.bandLocal

function bandText(build?: { date: string, commit: string }): string {
    if (!build?.date) return LOKAL
    const d = new Date(build.date)
    // An unparsable stamp falls back to the plain sentence rather than painting
    // "Invalid Date" across the top of the screen.
    if (Number.isNaN(d.getTime())) return LOKAL
    const s = new Intl.DateTimeFormat('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(d)
    const sha = build.commit ? ` · ${build.commit.slice(0, 7)}` : ''
    return fill(T.bandBuild, { date: s, sha })
}

// The dev marker: a violet band across the top of every non-production instance, so it is
// obvious at a glance that this is not the live backoffice.
//
// ⚠️ It used to be a bare 5px stripe, and it was measured to be genuinely there and
// genuinely violet on localhost:4000 — the operator simply did not notice it. 5px carry no
// message. That is why the band now always carries text, and why the stripe-only shape is
// gone rather than kept "for the local case".
//
// Deliberately driven by the SERVER's mode (GET /api/health, public — so it is there on
// the login screen too), not by the domain: dev is reached under more than one name, and
// a hostname check would quietly be wrong the day another one is added.
//
// ⚠️ Why the band is `position: fixed` even though it must NOT cover anything: the login
// screen is `position: fixed; inset: 0`, so a band in normal flow would not push it down
// at all — it would only work on half the screens. Instead the height lives in ONE place,
// the custom property `--band-h` on the frame below, and `.shell` subtracts it from its
// own 100vh (styles.css). Nothing is covered, and there is no second number to keep in
// step. See DEPLOY.md for the workflow half.
// The test-mail mode rides on the SAME band (one height, one place — see `--band-h`), on every
// instance, production included: the operator tests there before go-live, and the band is what
// keeps him from forgetting the mode is on. The address comes from behind the login.
const mailModeOn = (h: Health | null) => h?.mailRedirect === 'on' || h?.mailRedirect === 'missing-address'

function mailBandText(h: Health, to: string): string {
    if (h.mailRedirect === 'missing-address') return T.mailBandMissingAddress
    if (h.mailRedirect === 'on') return fill(T.mailBandOn, { to: to || T.mailBandDefaultTo })
    return ''
}

function DevMarker({ health }: { health: Health | null }) {
    const { user } = useAuth()
    const [mailTo, setMailTo] = useState('')
    const mailOn = mailModeOn(health)
    useEffect(() => {
        if (!user || !mailOn) { setMailTo(''); return }
        getMailMode().then((m) => setMailTo(m.to)).catch(() => setMailTo(''))
    }, [user, mailOn])
    if (!health) return null
    const envText = health.mode !== 'production' ? bandText(health.build) : ''
    const mailText = mailBandText(health, mailTo)
    if (!envText && !mailText) return null
    // ⚠️ The band shows LOCAL time (Intl above) — right for a human, but the build logs
    // and the cron are in UTC, and this project spends real time on exactly that offset.
    // So the raw stamp goes into the tooltip: the friendly number to read, the exact one
    // to compare against `gh run list`.
    const stamp = health.build?.date ? fill(T.builtAt, { date: health.build.date }) : ''
    // ⚠️ The tooltip REUSES the band text instead of wording the same thing a second way.
    // The operator quoted the old tooltip when he asked for the band's wording — the two
    // had drifted apart, and one name for one thing is the fix. Only the extras differ.
    const text = [envText, mailText].filter(Boolean).join(' · ')
    return (
        <div className="dev-stripe" title={`${text} · ${health.mode}${stamp}`}>
            {text}
        </div>
    )
}

export default function App() {
    const [health, setHealth] = useState<Health | null>(null)
    // Re-read every minute: the test-mail switch can be flipped while the app is open, and the
    // band must follow without a reload.
    useEffect(() => {
        const load = () => getHealth().then(setHealth).catch(() => setHealth(null))
        load()
        const t = setInterval(load, BUILD_INFO_POLL_MS)
        return () => clearInterval(t)
    }, [])

    // `has-dev-band` carries `--band-h`. ⚠️ It goes on the ROOT element, not on a wrapper
    // div: every `position: fixed` element that has to start below the band needs to read
    // it (the toast container, the drawers — styles.css), and those are not all inside one
    // subtree. On a wrapper it silently resolved to the 0 fallback for anything outside,
    // and a toast then slid under the band. Custom properties inherit down the DOM tree,
    // so the root is the only place that reaches all of them.
    const banded = !!health && (health.mode !== 'production' || mailModeOn(health))
    useEffect(() => {
        document.documentElement.classList.toggle('has-dev-band', banded)
        return () => document.documentElement.classList.remove('has-dev-band')
    }, [banded])

    return (
        <AuthProvider>
            <div className="app-frame">
                <DevMarker health={health} />
                <Gate />
            </div>
        </AuthProvider>
    )
}
